import { z } from "zod";
import type { Chunk } from "./ai";
import { formatChunks } from "./generation/prompts";
import { resolveRefs } from "./generation/assemble";

/**
 * Ask Hoot (spec §6.5): a health-domain gate first, then labelled answer classes. Material answers are
 * always tried first and must cite the set's chunks; general knowledge and mnemonics are Pro and labelled.
 */

export const OUT_OF_SCOPE_REPLY = "Sorry, I can only help with health-sciences study questions.";
export const DISCLAIMER = "StudyWard is a study aid, not medical advice.";
export const PATIENT_ADVICE_REPLY = `${OUT_OF_SCOPE_REPLY} I can't advise on a real person's care — please ask a clinician. ${DISCLAIMER}`;
export const NOT_IN_MATERIAL_REPLY = "That isn't covered in your material for this set, so I won't guess.";
export const GENERAL_KNOWLEDGE_LABEL = "General medical knowledge — not from your material. Verify with your textbook.";
export const MNEMONIC_LABEL = "Memory aid";

export const TUTOR_KINDS = ["ask", "hint", "explain_simpler", "mnemonic"] as const;
export const tutorKindSchema = z.enum(TUTOR_KINDS);
export type TutorKind = z.infer<typeof tutorKindSchema>;

export const ANSWER_CLASSES = ["material", "general_knowledge", "mnemonic", "not_in_material", "out_of_scope"] as const;
export const answerClassSchema = z.enum(ANSWER_CLASSES);
export type AnswerClass = z.infer<typeof answerClassSchema>;

export const tutorMessageRequestSchema = z.object({
  thread_id: z.string().uuid().nullable().default(null),
  content: z.string().trim().min(1, "Type a question").max(1000),
  kind: tutorKindSchema.default("ask"),
  /** Practice hints / "Explain simpler" are about one question. */
  question_id: z.string().uuid().nullable().default(null),
  /** Pro users can switch general-knowledge answers off (§6.5 class 4). */
  allow_general_knowledge: z.boolean().default(true),
});
export type TutorMessageRequest = z.infer<typeof tutorMessageRequestSchema>;

export const tutorMessageSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(["user", "assistant"]),
  kind: tutorKindSchema,
  content: z.string(),
  answer_class: answerClassSchema.nullable(),
  cited_chunk_ids: z.array(z.string().uuid()),
  created_at: z.string(),
});
export type TutorMessage = z.infer<typeof tutorMessageSchema>;

export const tutorThreadSchema = z.object({
  thread_id: z.string().uuid().nullable(),
  messages: z.array(tutorMessageSchema),
  /** Questions used on this set; `allowed` null = unlimited (Pro). */
  usage: z.object({ used: z.number().int(), allowed: z.number().int().nullable() }),
});
export const tutorReplySchema = z.object({
  thread_id: z.string().uuid(),
  user_message: tutorMessageSchema,
  reply: tutorMessageSchema,
  usage: z.object({ used: z.number().int(), allowed: z.number().int().nullable() }),
});
export type TutorReply = z.infer<typeof tutorReplySchema>;

/** What the model returns: the gate verdict and one answer (§6.5). */
export const tutorOutputSchema = z.object({
  scope: z.enum(["in_scope", "out_of_scope", "patient_advice"]),
  answer_class: z.enum(["material", "general_knowledge", "mnemonic", "not_in_material"]),
  content: z.string().max(4000),
  chunk_refs: z.array(z.string()),
});
export type TutorOutput = z.infer<typeof tutorOutputSchema>;

export interface TutorPromptInput {
  examName: string;
  chunks: Chunk[];
  history: Array<{ role: "user" | "assistant"; content: string }>;
  question: string;
  kind: TutorKind;
  allowGeneral: boolean;
  allowMnemonic: boolean;
  /** The practice question a hint or simpler explanation is about. */
  item?: unknown;
}

export function buildTutorPrompt(input: TutorPromptInput): string {
  const task = {
    ask: "Answer the student's question.",
    hint: "Give ONE short Socratic hint that points toward the reasoning without revealing the answer or naming the correct option.",
    explain_simpler: "Explain this question's correct answer and reasoning again in plain, simpler words, as if the student is tired.",
    mnemonic: "Give a short memory aid (mnemonic) for the student's topic.",
  }[input.kind];
  return `You are Hoot, a study helper for a ${input.examName} student. You only help with health-sciences study.

Step 1 — scope:
- "out_of_scope": anything that isn't health sciences or the student's studies (coding, other subjects, news, personal chat).
- "patient_advice": asking what to do for a real person (themselves, family, a patient they care for). Never answer it.
- otherwise "in_scope".
If not in scope, set content to "" and chunk_refs to [].

Step 2 — answer (in scope only). ${task}
- First try the student's material below. If it covers the answer, answer_class = "material", cite the chunk refs (c1, c2 …) for every claim, and use nothing outside them.
- If the material doesn't cover it: ${
    input.allowGeneral
      ? 'answer from general medical knowledge with answer_class = "general_knowledge" and chunk_refs = []. Never pretend it came from the material.'
      : 'answer_class = "not_in_material", content = "", chunk_refs = []. Do not guess.'
  }
${input.allowMnemonic ? '- A memory aid uses answer_class = "mnemonic"; any facts it encodes must come from the cited material.' : ""}
- Study material only: never give clinical advice for a real patient. Plain, encouraging language; keep it under 180 words.
${input.item ? `\nThe practice question:\n${JSON.stringify(input.item)}\n` : ""}
Conversation so far:
${input.history.map((m) => `${m.role === "user" ? "Student" : "Hoot"}: ${m.content}`).join("\n") || "(new conversation)"}

Student: ${input.question}

Student's material:
${formatChunks(input.chunks)}`;
}

export interface ResolvedTutorReply {
  answer_class: AnswerClass;
  content: string;
  cited_chunk_ids: string[];
  /** Out-of-scope and patient-advice questions don't count toward the tutor cap (§6.5). */
  counts: boolean;
}

/**
 * The server, not the model, decides what is shown (§6.5): gated questions get the fixed reply, Pro-only
 * classes fall back to "not in your material" for Free, and a "material" answer without a real citation is
 * never presented as coming from the material.
 */
export function resolveTutorReply(out: TutorOutput, ctx: { chunkIds: string[]; allowGeneral: boolean; allowMnemonic: boolean }): ResolvedTutorReply {
  if (out.scope === "out_of_scope") return { answer_class: "out_of_scope", content: OUT_OF_SCOPE_REPLY, cited_chunk_ids: [], counts: false };
  if (out.scope === "patient_advice") return { answer_class: "out_of_scope", content: PATIENT_ADVICE_REPLY, cited_chunk_ids: [], counts: false };
  const notInMaterial: ResolvedTutorReply = { answer_class: "not_in_material", content: NOT_IN_MATERIAL_REPLY, cited_chunk_ids: [], counts: true };
  const content = out.content.trim();
  const cited = resolveRefs(out.chunk_refs.filter((r) => /^c\d+$/.test(r)), ctx.chunkIds);
  switch (out.answer_class) {
    case "material":
      return content && cited.length ? { answer_class: "material", content, cited_chunk_ids: cited, counts: true } : notInMaterial;
    case "general_knowledge":
      return ctx.allowGeneral && content ? { answer_class: "general_knowledge", content, cited_chunk_ids: [], counts: true } : notInMaterial;
    case "mnemonic":
      return ctx.allowMnemonic && content ? { answer_class: "mnemonic", content, cited_chunk_ids: cited, counts: true } : notInMaterial;
    case "not_in_material":
      return notInMaterial;
  }
}

// ---------------------------------------------------------------- patient cases (§6.8, screen #17, Pro)
const caseRef = z.string().regex(/^c\d+$/);
export const caseOutputSchema = z.object({
  title: z.string().min(1).max(100),
  scenario: z.string().min(1).max(1500),
  steps: z
    .array(
      z.object({
        update: z.string().max(800).describe("What changes for the patient before this decision; empty for the first step"),
        prompt: z.string().min(1).max(400),
        choices: z.array(z.object({ id: z.string().min(1).max(10), text: z.string().min(1).max(200), points: z.number().int().min(-2).max(2), rationale: z.string().min(1).max(600) })).min(3).max(5),
        chunk_refs: z.array(caseRef).min(1),
      }),
    )
    .min(3)
    .max(6),
});
export type CaseOutput = z.infer<typeof caseOutputSchema>;

export function buildCasePrompt(input: { examName: string; chunks: Chunk[]; avoidTitles: string[] }): string {
  return `Write one fictional, branching patient case for a ${input.examName} student, built ONLY from the numbered chunks below.
- 3 to 6 decision steps. Each step: an optional patient update, a decision prompt, 3–5 choices.
- Score each choice: 2 = best, 1 = acceptable, 0 = unhelpful, -1 or -2 = harmful. Exactly one choice per step scores 2.
- Each choice's rationale says why, citing the chunks. Cite chunk refs (c1, c2 …) on every step.
- The patient is fictional: no real names or identifiers. Study material only — not clinical advice.
- Don't invent findings, values or doses that aren't in the chunks.
${input.avoidTitles.length ? `- Make it different from these existing cases: ${input.avoidTitles.join("; ")}` : ""}

Chunks:
${formatChunks(input.chunks)}`;
}

export const patientCaseSchema = z.object({
  id: z.string().uuid(),
  set_id: z.string().uuid(),
  title: z.string(),
  scenario: z.string(),
  steps: z.array(
    z.object({
      update: z.string(),
      prompt: z.string(),
      choices: z.array(z.object({ id: z.string(), text: z.string() })),
      source_chunk_ids: z.array(z.string().uuid()),
    }),
  ),
  attempt: z
    .object({
      decisions: z.array(z.object({ step: z.number().int(), choice_id: z.string(), points: z.number().int() })),
      score_percent: z.number().int().nullable(),
      completed: z.boolean(),
    })
    .nullable(),
});
export type PatientCase = z.infer<typeof patientCaseSchema>;
export const patientCaseListSchema = z.object({ items: z.array(patientCaseSchema) });
export const caseDecisionRequestSchema = z.object({ step: z.number().int().min(0).max(10), choice_id: z.string().min(1).max(10) });
export const caseDecisionResponseSchema = z.object({
  points: z.number().int(),
  best_choice_id: z.string(),
  rationale: z.string(),
  choice_rationales: z.record(z.string(), z.string()),
  completed: z.boolean(),
  score_percent: z.number().int().nullable(),
});

// ---------------------------------------------------------------- Diagram Lab (§6.6, screen #16)
const unit = z.number().min(0).max(1);
export const occlusionRegionSchema = z.object({ id: z.string().min(1).max(20), label: z.string().trim().min(1).max(80), x: unit, y: unit, w: unit, h: unit });
export type OcclusionRegion = z.infer<typeof occlusionRegionSchema>;
export const visionRegionsOutputSchema = z.object({
  regions: z.array(z.object({ label: z.string().min(1).max(80), x: unit, y: unit, w: unit, h: unit })).max(30),
});
export const DIAGRAM_REGIONS_PROMPT =
  "This is a student's own study diagram. Find each printed text label already on the image (anatomical or equipment labels). For each, return the label text and a box around it as fractions of the image width and height (x, y = top-left corner). Only report labels that are visibly printed on the image; do not invent or add labels.";
export const createDiagramRequestSchema = z.object({ source_id: z.string().uuid(), credit: z.string().trim().max(200).optional() });
export const occlusionsRequestSchema = z.object({ regions: z.array(occlusionRegionSchema).max(30) });
export const diagramSchema = z.object({
  id: z.string().uuid(),
  set_id: z.string().uuid(),
  image_url: z.string().url(),
  credit: z.string().nullable(),
  regions: z.array(occlusionRegionSchema),
  created_at: z.string(),
});
export type Diagram = z.infer<typeof diagramSchema>;
export const diagramListSchema = z.object({
  items: z.array(diagramSchema),
  usage: z.object({ used: z.number().int(), allowed: z.number().int().nullable(), resets_at: z.string() }),
});
